/* eslint-disable @typescript-eslint/no-explicit-any */
import { Star } from '@/styles/Icon'
import type { ProductReview } from '@/types'
import { getAllProductReview } from '@/utils/endpoint'
import { Tab } from '@headlessui/react'
import dynamic from 'next/dynamic'
import { Fragment, useEffect, useState } from 'react'
import ImageOptimize from '../ImageOptimize'
import ReviewAndRateItem from '../ReviewAndRateItem'

const StarRatings = dynamic(() => import('react-star-ratings'), {
  ssr: false,
})

interface ReviewAndRateType {
  productId: string
  reviewSummary: number[]
  average?: number
  onOpenCreateModal: () => void
}

const filterAttr = [
  { name: 'Mới nhất', type: 0, action: 'sort' },
  { name: 'Có hình ảnh', type: 0, action: 'hasImage' },
  { name: '5 Sao', type: 5, action: 'point' },
  { name: '4 Sao', type: 4, action: 'point' },
  { name: '3 Sao', type: 3, action: 'point' },
  { name: '2 Sao', type: 2, action: 'point' },
]

export const ProductReviewAndRateModal = ({
  productId,
  reviewSummary,
  average,
  onOpenCreateModal,
}: ReviewAndRateType) => {
  const [filterItemtReview, setFilterItemReview] = useState<ProductReview[]>([])

  const sum = reviewSummary?.reduce((acc, curr) => acc + curr, 0)
  const newArrayPoint = reviewSummary.map((value, index) => {
    return {
      point: index + 1,
      value: value,
    }
  })

  useEffect(() => {
    getAllProductReview(`productId=${productId}`).then(async result =>
      setFilterItemReview(result?.data || [])
    )
  }, [])

  const changeDataReview = (e: {
    name?: string
    type: number
    action: string
  }) => {
    setFilterItemReview([])
    switch (e?.action) {
      case 'sort':
        getAllProductReview(
          `productId=${productId}&sortBy=createdAt&sortType=desc`
        ).then(result => setFilterItemReview(result?.data || []))
        break
      case 'hasImage':
        getAllProductReview(`productId=${productId}&hasImage=true`).then(
          result => setFilterItemReview(result?.data || [])
        )
        break
      case 'point':
        getAllProductReview(`productId=${productId}&point=${e?.type}`).then(
          result => setFilterItemReview(result?.data || [])
        )
        break
      default:
        getAllProductReview(
          `productId=${productId}&sortBy=createdAt&sortType=desc`
        ).then(result => setFilterItemReview(result?.data || []))
        break
    }
  }

  return (
    <div className='w-full'>
      <div className='sticky top-0 z-[1] pb-4'>
        <button
          className='flex h-12 w-full items-center justify-between rounded-sm bg-[#F3F4F6] px-3 text-sm text-[#6B7280] outline-none'
          onClick={onOpenCreateModal}
        >
          Chia sẻ đánh giá của bạn
          <img src='/assets/icons/review-icon.svg' alt='give review' />
        </button>
        <div className='overView mt-3 flex rounded-lg border border-[#D1D5DB] pt-3'>
          <div className='star_average relative flex max-w-[30%] flex-[30%] flex-col items-center justify-center border-r border-[#ECECEC]'>
            <div className='text-black flex gap-2 text-center text-2xl font-bold'>
              <span className='current'>{average ? average : '5'}</span>
              <span className='seperate'>/</span>
              <span className='limit'>5</span>
            </div>
            <div className='mt-2'>
              <StarRatings
                starRatedColor='#FFCD4B'
                numberOfStars={5}
                rating={average ? average : 5}
                starDimension='14px'
                starSpacing='1px'
                svgIconPath='M20 0L26.1803 13.1672L40 15.2786L30 25.5279L32.3607 40L20 33.1672L7.63932 40L10 25.5279L0 15.2786L13.8197 13.1672L20 0Z'
                svgIconViewBox='0 0 40 40'
              />
            </div>
            {!!sum && (
              <div className='mt-1'>
                <span className='text-sm text-sub'>
                  {`${sum ? sum : 0} đánh giá`}
                </span>
              </div>
            )}
          </div>

          <div className='star_lines flex max-w-[70%] flex-[70%] flex-col gap-1 pb-3 pl-2'>
            {newArrayPoint?.reverse().map(point => (
              <div
                className='star_line flex items-center gap-2 pr-3 text-xs leading-4'
                key={point.point}
              >
                <div className='star_type flex w-5 items-center gap-[3px] text-[#555555]'>
                  <span>{point.point}</span>
                  <Star />
                </div>
                <div className='prog flex-auto relative h-1.5 w-full rounded-[10px] bg-zinc-300'>
                  <div
                    className='prog_bar absolute h-1.5 rounded-[10px] bg-amber-300'
                    style={{
                      width:
                        sum === 0 && point.point === 5
                          ? '100%'
                          : (point.value / (sum || 1)) * 100 + '%',
                    }}
                  />
                </div>
                <span className='min-w-[40px] text-right text-main'>
                  {sum === 0 && point.point === 5
                    ? 100
                    : (point.value / (sum || 1)) * 100}
                  %
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className='mt-4 text-sm font-semibold text-main'>
          Lọc xem theo:
        </div>
        <Tab.Group>
          <div className='sticky top-0 z-10 mt-3 bg-white pb-2 pt-[1px]'>
            <Tab.List className='filterReview flex items-center gap-2' as='div'>
              <div className='filterItems flex w-full flex-wrap items-center justify-start gap-2'>
                {filterAttr.map((e, i) => {
                  return (
                    <Tab as={Fragment} key={i}>
                      {({ selected }) => (
                        <button
                          onClick={() => changeDataReview(e)}
                          className={`${
                            selected
                              ? 'border border-[#003DD4] bg-[#F7F8F8] text-[#003DD4]'
                              : 'bg-[#F3F4F6] text-[#555555]'
                          } h-[30px] rounded px-2 text-sm hover:border-[#003DD4] hover:bg-[#F7F8F8] hover:text-[#003DD4]`}
                        >
                          {e?.name}
                        </button>
                      )}
                    </Tab>
                  )
                })}
              </div>
            </Tab.List>
          </div>

          <Tab.Panels>
            {filterAttr.map((_, i) => {
              return (
                <Tab.Panel key={i} className='border-0 outline-none'>
                  <div className='comments mt-3 flex flex-col flex-wrap gap-3'>
                    {filterItemtReview.length > 0 ? (
                      filterItemtReview.map((e, i) => (
                        <ReviewAndRateItem
                          key={e._id}
                          data={e}
                          bottomDivider={i < filterItemtReview.length - 1}
                        />
                      ))
                    ) : (
                      <div className='mt-8 flex flex-col items-center gap-6'>
                        <ImageOptimize
                          src='/assets/images/empty-review.png'
                          alt='empty review'
                          width={100}
                          height={100}
                        />
                        <p className='font-sm text-main'>Chưa có đánh giá</p>
                      </div>
                    )}
                  </div>
                </Tab.Panel>
              )
            })}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  )
}

export default ProductReviewAndRateModal
